import React, { useState, useContext } from 'react'
import { useLocation } from '@reach/router'
import { Grid, Cell, Button } from 'react-foundation'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { memberID, medicaidID } from '../../../Validations'
import { ApiFetch } from '../Api'
import { ServiceRequestMember } from '../../../models'
import { InputFormGroup, SelectFieldGroup } from './FormFields'
import UserContext from '../../../UserContext'
import { SaveEvent, ClickEvent } from '../AnalyticsEvents'
import { Roles } from '../../../UserContext'
import { DEFAULT_STATE, SUPPORTED_STATES } from '../../Preferences/stateServiceAreaConfig'

type MemberSearchProps = {
    serviceRequestMember?: ServiceRequestMember
    onMemberSelected: React.Dispatch<ServiceRequestMember>
    includeAddress: boolean
}

const MemberSearch: React.FC<MemberSearchProps> = ({ serviceRequestMember, onMemberSelected, includeAddress }) => {
    const [memberLocked, setMemberLocked] = useState<boolean>(serviceRequestMember ? true : false)
    const [memberSearchError, setMemberSearchError] = useState<string>('')
    const user = useContext(UserContext)
    const { pathname } = useLocation()

    const [lastIdValue, setLastIdValue] = useState<string>()

    const initialValues: ServiceRequestMember = {
        memberID: serviceRequestMember ? serviceRequestMember.memberID ?? '' : '',
        medicaidID: serviceRequestMember ? serviceRequestMember.medicaidID ?? '' : '',
        name: serviceRequestMember ? serviceRequestMember.name ?? '' : '',
        gender: serviceRequestMember ? serviceRequestMember.gender ?? '' : '',
        plan: serviceRequestMember ? serviceRequestMember.plan ?? '' : '',
        state: serviceRequestMember ? serviceRequestMember.state ?? '' : '',
        county: serviceRequestMember ? serviceRequestMember.county ?? '' : '',
        zipCode: serviceRequestMember ? serviceRequestMember.zipCode ?? '' : '',
        address: serviceRequestMember ? serviceRequestMember.address ?? '' : '',
        city: serviceRequestMember ? serviceRequestMember.city ?? '' : '',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        phone: serviceRequestMember ? serviceRequestMember.phone ?? '' : '',
        birthDate: serviceRequestMember ? serviceRequestMember.birthDate ?? new Date() : new Date(),
        lookupIDType: serviceRequestMember ? serviceRequestMember.lookupIDType ?? 'Member' : 'Member',
        medicaidState: serviceRequestMember ? serviceRequestMember.medicaidState ?? DEFAULT_STATE : DEFAULT_STATE
    }

    const validationScheme = Yup.object({ memberID, medicaidID })

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            onSubmit={(values, { setSubmitting, setValues }) => {

                setSubmitting(true)

                const id = values && values.memberID ? values.memberID : values.medicaidID
                let url = `member/${id ?? 0}/${values.lookupIDType ?? ''}`
                if (values.medicaidState) {
                    url += '/' + values.medicaidState
                }

                ApiFetch<ServiceRequestMember>(url)
                    .then(
                        (result) => {
                            setValues({ ...initialValues, ...result })
                            setSubmitting(false)
                            setMemberSearchError('')
                        },
                        () => {
                            setValues({ ...values })
                            setMemberSearchError('Member not found')
                            setSubmitting(false)
                        }
                    )
            }
            }>
            {(formikProps) => {
                const { submitForm, setValues, values, initialValues, isSubmitting, handleChange } = formikProps

                const onMemberIDKeyUp = () => {
                    //Key press didn't change anything
                    if (lastIdValue === values.memberID)
                        return
                    setLastIdValue(values.memberID)

                    //Not a valid ID yet
                    let doSubmit = true
                    if (values.memberID?.length !== 11)
                        doSubmit = false

                    setMemberSearchError('')
                    setValues({
                        ...initialValues,
                        lookupIDType: 'Member',
                        memberID: values.memberID
                    })
                    if (doSubmit)
                        // eslint-disable-next-line no-console
                        submitForm().catch(console.log)
                }

                const onMedicaidIDKeyUp = () => {
                    //Key press didn't change anything
                    if (lastIdValue === values.medicaidID)
                        return
                    setLastIdValue(values.medicaidID)

                    //Not a valid ID yet
                    let doSubmit = true
                    if (values.medicaidID?.length !== 12)   //This is the length for OH!
                        doSubmit = false

                    setMemberSearchError('')
                    setValues({
                        ...initialValues,
                        lookupIDType: 'Medicaid',
                        medicaidID: values.medicaidID
                    })
                    if (doSubmit)
                        // eslint-disable-next-line no-console
                        submitForm().catch(console.log)
                }

                return (
                    <Form className="member-search">
                        {memberLocked && <h5><i className="fas fa-user"></i>Member Information</h5>}
                        {!memberLocked && <h5><i className="fas fa-user"></i>Member Lookup</h5>}
                        {!user.hasAnyRole(Roles.Provider) && ( 
                        <Grid>
                            <Cell small={5} large={5} className="errorText">
                                <ErrorMessage name="memberID" />
                                {memberSearchError}
                            </Cell>
                            <Cell small={7} large={7} className="errorText">
                                <ErrorMessage name="medicaidID" />
                            </Cell>
                            <Cell small={4} large={4}>
                                <label htmlFor="memberID">Member ID</label>
                                <Field
                                    name="memberID"
                                    type="text"
                                    disabled={memberLocked || isSubmitting}
                                    onChange={(e: React.ChangeEvent<string>) => {
                                        setValues({
                                            ...initialValues,
                                            ...values,
                                            medicaidID: '',
                                            lookupIDType: 'Member'
                                        })
                                        handleChange(e)
                                    }}
                                    onKeyUp={onMemberIDKeyUp} />
                            </Cell>
                            <Cell small={1} large={1} className="separator-cell">
                                {!memberLocked && 'or'}
                            </Cell>
                            <Cell small={4} large={4}>
                                <label htmlFor="medicaidID">Medicaid ID</label>
                                <Field
                                    name="medicaidID"
                                    type="text"
                                    disabled={memberLocked || isSubmitting}
                                    onChange={(e: React.ChangeEvent<string>) => {
                                        setValues({
                                            ...initialValues,
                                            ...values,
                                            memberID: '',
                                            lookupIDType: 'Medicaid'
                                        })
                                        handleChange(e)
                                    }}
                                    onKeyUp={onMedicaidIDKeyUp} />
                            </Cell>
                            <Cell small={2} large={2}>
                                <SelectFieldGroup name='medicaidState' title='State' disabled={memberLocked || isSubmitting} readableSeperator={' - '} options={[...SUPPORTED_STATES]} />
                            </Cell>
                        </Grid>
                        )}
                        <Grid>
                            <Cell small={12} large={5}>
                                <InputFormGroup noError name='name' title='Name' disabled={true} />
                            </Cell>
                            {!user.hasAnyRole(Roles.Provider) && ( 
                            <Cell small={12} large={5}>
                                <InputFormGroup noError name='plan' title='Plan' disabled={true} />
                            </Cell>
                            )}
                            <Cell small={6} large={2}>
                                <InputFormGroup noError name='gender' title='Gender' disabled={true} />
                            </Cell>
                            {includeAddress && <>
                                <Cell small={12} large={8}>
                                    <InputFormGroup noError name='address' title='Address' disabled={true} />
                                </Cell>
                                <Cell small={12} large={4}>
                                    <InputFormGroup noError name='city' title='City' disabled={true} />
                                </Cell>
                            </>}
                            <Cell small={6} large={6}>
                                <InputFormGroup noError name='county' title='County' disabled={true} />
                            </Cell>
                            <Cell small={2} large={2}>
                                <InputFormGroup noError name='state' title='State' disabled={true} />
                            </Cell>
                            <Cell small={4} large={4}>
                                <InputFormGroup noError name='zipCode' title='Zip' disabled={true} />
                            </Cell>
                            <Cell small={4} large={4}>
                                <InputFormGroup noError name='phone' title='Phone' disabled={true} />
                            </Cell>
                        </Grid>
                        {!memberLocked && <div className="right">
                            <Button isDisabled={!(values && values.name) ? true : false}
                                onClick={() => {
                                    if (values && values.name) {
                                        SaveEvent(ClickEvent(user, pathname, 'Member_Confirm', values))
                                        setMemberLocked(true)
                                        onMemberSelected({ ...values })
                                    }
                                }}>
                                Confirm Member
                            </Button>
                        </div>}
                    </Form>
                )
            }}
        </Formik>
    )
}

export default MemberSearch
